import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  GetDataSourceJsonPresignedUrlDocument,
  type GetDataSourceJsonPresignedUrlQuery,
  type GetDataSourceJsonPresignedUrlQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { downloadFile } from "~/utils/download";

/**
 * Downloads the S3-backed JSON payload of a JSON data source. The s3Path is
 * not directly accessible from the browser, so this fetches a short-lived
 * presigned URL from Cnut and triggers a browser download of the gzip object.
 */
export const useDataSourceJsonDownload = () => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [downloading, setDownloading] = useState(false);
  const [getPresignedUrl] = useLazyQuery<
    GetDataSourceJsonPresignedUrlQuery,
    GetDataSourceJsonPresignedUrlQueryVariables
  >(GetDataSourceJsonPresignedUrlDocument, { fetchPolicy: "no-cache" });

  const onDownloadClick = useCallback(
    async (dataSource: { id: string; name: string }) => {
      setDownloading(true);
      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const { data } = await getPresignedUrl({ variables: { input: { tenantId, dataSourceId: dataSource.id } } });
        const result = data?.cnutGetDataSourceJsonPresignedUrl;
        if (result?.__typename === "GetDataSourceJsonPresignedUrlOutput") {
          // the presigned URL carries Content-Disposition: attachment, so S3
          // drives the actual download + filename; this name is the fallback.
          downloadFile(result.url, `${dataSource.name}.json`);
        } else {
          toast.error(t("feature.dataImportProjects.detail.dataSource.alert.jsonDownloadFailed"));
        }
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setDownloading(false);
      }
    },
    [getPresignedUrl, tenantId, t],
  );

  return { onDownloadClick, downloading };
};
